// gioco/usaClip.ts — la clip della risata nella partita (v008): consenso, registrazione durante il round, montaggio
// a fine round. Si fa SOLO se tutti e due i giocatori hanno detto sì (lo sa il server, vista.clip) e se il telefono
// sa fare le clip. Il video resta sul telefono: si condivide o si scarica solo se chi gioca lo decide.
import { useCallback, useEffect, useRef, useState } from "react";
import { creaRegistratoreClip, supportoClip, ErroreClip, type ClipPronta, type RegistratoreClip } from "../clip/clip.ts";
import testiClip from "../clip/i18n.json";
import { tempoReale } from "../servizi/tempoReale.ts";
import { eBooleano, eNumero, leggiImpostazione } from "../servizi/impostazioni.ts";
import { indirizzoSito } from "./Condividi.tsx";
import { IMP_APP, PREFERENZE } from "../costanti.ts";
import { leggiPreferenza, salvaPreferenza } from "../servizi/preferenze.ts";
import { stanza } from "./stanza.ts";
import { eRtl } from "../../../condiviso/lingue.ts";
import { altro, type Ruolo, type VistaPartita } from "../../../condiviso/partita.ts";

export type StatoClip =
  | { s: "spenta" } // impostazione spenta o telefono che non sa fare clip
  | { s: "attesa" } // manca il consenso di qualcuno, o il round non è ancora iniziato
  | { s: "registra" }
  | { s: "pronta_da_creare" }
  | { s: "creazione"; avanzamento: number }
  | { s: "fatta"; clip: ClipPronta; url: string; solo: boolean }
  | { s: "errore"; codice: string };

interface Ingressi {
  vista: VistaPartita | null;
  io: Ruolo;
  remoto: MediaStream | null;
  invio: MediaStream;
  lingua: string;
  /** link nel finale della clip («Sfidami: …») */
  link: string | null;
  /** non più usato dalla v038 (niente disegno al posto dell'altro) */
  nomeLui?: string;
}

const T = testiClip as Record<string, { haRiso: string; haVinto: string; via: string; sfidami: string }>;

/** Le opzioni del motore delle clip (v042: esportate, le usa anche la partita a 4). */
export function opzioni(lingua: string, link: string) {
  const n = (k: string, r: number) => leggiImpostazione(k, r, eNumero);
  const stile = getComputedStyle(document.documentElement);
  const c = (v: string, r: string) => stile.getPropertyValue(v).trim() || r;
  return {
    larghezza: 720,
    altezza: 1280,
    fps: 30,
    secondiMassimi: n(IMP_APP.clipSecondiMassimi, 20),
    secondiPrimaRisata: n(IMP_APP.clipSecondiPrimaRisata, 10),
    rallentatore: n(IMP_APP.clipRallentatorePercento, 50) / 100,
    accelerato: leggiImpostazione(IMP_APP.clipAccelerato, false, eBooleano),
    secondiRallentatore: n(IMP_APP.clipSecondiRallentatore, 3),
    secondiFinale: n(IMP_APP.clipSecondiFinale, 2),
    audio: leggiImpostazione(IMP_APP.clipAudio, true, eBooleano),
    colori: {
      sfondo: c("--c-sfondo", "#ffd84d"),
      testo: c("--c-testo", "#1e1433"),
      primario: c("--c-primario", "#ff5a5f"),
      suPrimario: c("--c-su-primario", "#ffffff"),
      accento: c("--c-accento", "#3ec9f0"),
      pericolo: c("--c-pericolo", "#d62839"),
      successo: c("--c-successo", "#0b6b4f"),
    },
    caratteri: { titolo: c("--f-titolo", "system-ui"), testo: c("--f-testo", "system-ui") },
    testi: T[lingua] ?? T.en!,
    link,
    simbolo: "",
    rtl: eRtl(lingua),
  };
}

let simbolo: Promise<string> | null = null;
export const caricaSimbolo = () => (simbolo ??= fetch("/icona.svg").then((r) => (r.ok ? r.text() : "")).catch(() => ""));

/** v035: la scelta sul video delle risate, fatta una volta (null = mai chiesto). */
export function sceltaClip(): boolean | null {
  const v = leggiPreferenza(PREFERENZE.clip);
  return v === "si" ? true : v === "no" ? false : null;
}
export function salvaSceltaClip(si: boolean): void {
  salvaPreferenza(PREFERENZE.clip, si ? "si" : "no");
}
/** I video delle risate sono accesi nel pannello e il telefono li sa fare. */
export function clipPossibili(): boolean {
  return leggiImpostazione(IMP_APP.clipAttive, false, eBooleano) && supportoClip().possibile;
}

export function useClip({ vista, io, remoto, invio, lingua, link }: Ingressi): {
  stato: StatoClip;
  crea: () => void;
  consenso: boolean;
  consensoAltro: boolean;
  disponibile: boolean;
  /** v037: il video di questo round ha solo la mia faccia (l'altro non ha detto sì) */
  solo: boolean;
} {
  const attive = leggiImpostazione(IMP_APP.clipAttive, false, eBooleano);
  const disponibile = attive && supportoClip().possibile;
  const [stato, setStato] = useState<StatoClip>(disponibile ? { s: "attesa" } : { s: "spenta" });
  const reg = useRef<RegistratoreClip | null>(null);
  const roundRif = useRef<string | null>(null);
  const eventi = useRef({ via: false, pausa: false, fine: false, risataT: null as number | null });
  const invioRif = useRef(invio);
  invioRif.current = invio;
  const urlRif = useRef<string | null>(null);
  // La faccina del finale della clip (testo SVG), scaricata una volta.
  const simboloRif = useRef("");
  useEffect(() => {
    if (disponibile) void caricaSimbolo().then((svg) => (simboloRif.current = svg));
  }, [disponibile]);
  const annulla = useRef<AbortController | null>(null);

  const consenso = vista?.clip?.[io] === true;
  const consensoAltro = vista?.clip?.[altro(io)] === true;
  // v035: la scelta fatta una volta vale per tutte le partite: il consenso si manda da solo (prima andava spuntato
  // ogni volta). Una volta per stanza; se chi gioca lo spegne qui, resta spento.
  const mandato = useRef(false);
  useEffect(() => {
    if (!disponibile || !vista || mandato.current) return;
    mandato.current = true;
    if (sceltaClip() === true && !consenso) stanza.consensoClip(true);
  }, [disponibile, vista, consenso]);
  const tutti = consenso && consensoAltro;
  // v037: con il mio sì e senza il suo il video si fa lo stesso, con la mia faccia sola (lui diventa il «mistero»)
  const soloRif = useRef(false);
  const fase = vista?.fase ?? "attesa";
  const round = vista ? `${vista.serie.numero}-${vista.round}` : null;

  const libera = useCallback(() => {
    annulla.current?.abort();
    reg.current?.libera();
    reg.current = null;
    if (urlRif.current) URL.revokeObjectURL(urlRif.current);
    urlRif.current = null;
  }, []);
  useEffect(() => libera, [libera]);

  // Nuovo round (conto alla rovescia): si riparte da capo, se tutti e due hanno detto sì.
  useEffect(() => {
    if (!disponibile || !vista || fase !== "conto" || roundRif.current === round) return;
    roundRif.current = round;
    libera();
    eventi.current = { via: false, pausa: false, fine: false, risataT: null };
    if (!consenso || (tutti && !remoto)) {
      setStato({ s: "attesa" });
      return;
    }
    const link2 = link ?? new URL(indirizzoSito()).host;
    const o = { ...opzioni(lingua, link2), simbolo: simboloRif.current };
    // v038: «solo mio» (solo la mia faccia e la mia voce, a tutto schermo) se l'altro non ha detto sì al video
    soloRif.current = !tutti;
    const r = creaRegistratoreClip({ sopra: soloRif.current ? null : remoto, sotto: invioRif.current }, { ...o, solo: soloRif.current }, { adesso: () => tempoReale.oraServer() });
    reg.current = r;
    r.avvia();
    setStato({ s: "registra" });
  }, [disponibile, vista, fase, round, tutti, consenso, remoto, link, lingua, libera]);

  // Consenso ritirato (in qualunque momento): la registrazione e la clip del round si buttano. Se il video è «solo
  // mio» conta solo il mio sì (l'altro non c'è); altrimenti servono tutti e due.
  const valido = consenso && (soloRif.current || tutti);
  const tuttiRif = useRef(valido);
  tuttiRif.current = valido;
  useEffect(() => {
    if (valido || !reg.current) return;
    libera();
    setStato({ s: "attesa" });
  }, [valido, libera]);

  // Il video inviato cambia durante il round (filtro acceso o spento, cambio faccia): la clip segue quello nuovo.
  useEffect(() => {
    reg.current?.cambiaVideo?.("sotto", invio);
  }, [invio]);
  useEffect(() => {
    if (remoto && !soloRif.current) reg.current?.cambiaVideo?.("sopra", remoto);
  }, [remoto]);

  // Gli eventi del round, dal server (stesso orologio: ora del server).
  useEffect(() => {
    const r = reg.current;
    if (!r || !vista) return;
    const ora = tempoReale.oraServer();
    if (!eventi.current.via && vista.tVia !== null && (fase === "gioco" || fase === "verifica" || fase === "accusa" || fase === "pausa")) {
      eventi.current.via = true;
      r.segna({ tipo: "via", t: vista.tVia });
    }
    if (fase === "pausa" && !eventi.current.pausa) {
      eventi.current.pausa = true;
      r.segna({ tipo: "pausa", t: ora });
    } else if (fase !== "pausa" && eventi.current.pausa) {
      eventi.current.pausa = false;
      r.segna({ tipo: "ripresa", t: ora });
    }
    // Il momento della risata: quando il server la vede (verifica o accusa), altrimenti al risultato.
    if ((fase === "verifica" || fase === "accusa") && eventi.current.risataT === null) eventi.current.risataT = ora;
    if (fase === "risultato" && !eventi.current.fine && vista.esito) {
      eventi.current.fine = true;
      const perdente = vista.esito.annullata ? null : vista.esito.perdente;
      const chi = perdente === null ? null : perdente === io ? "sotto" : "sopra";
      if (chi) r.segna({ tipo: "risata", t: eventi.current.risataT ?? ora, chi });
      r.segna({ tipo: "fine", t: ora, perdente: chi });
      void r.ferma().then(() => setStato(chi ? { s: "pronta_da_creare" } : { s: "attesa" }));
    }
  }, [vista, fase, io]);

  const crea = useCallback(() => {
    const r = reg.current;
    if (!r || !tuttiRif.current) return;
    const c = new AbortController();
    annulla.current = c;
    setStato({ s: "creazione", avanzamento: 0 });
    r.creaClip((p) => setStato({ s: "creazione", avanzamento: p }), c.signal)
      .then((clip) => {
        if (urlRif.current) URL.revokeObjectURL(urlRif.current);
        urlRif.current = URL.createObjectURL(clip.blob);
        setStato({ s: "fatta", clip, url: urlRif.current, solo: soloRif.current });
      })
      .catch((e: unknown) => setStato({ s: "errore", codice: e instanceof ErroreClip ? e.codice : "codifica" }));
  }, []);

  return { stato: disponibile ? stato : { s: "spenta" }, crea, consenso, consensoAltro, disponibile, solo: soloRif.current };
}

/** Nome del file della clip da scaricare o condividere. */
export function nomeFileClip(tipo: string): string {
  const estensione = tipo.includes("mp4") ? "mp4" : "webm";
  return `laughdown-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.${estensione}`;
}
